import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const STATUS_COLORS = {
  scheduled: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  completed: 'bg-gray-100 dark:bg-gray-800 text-gray-800',
  cancelled: 'bg-red-100 text-red-800',
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export default function SeriesDialog({ open, onOpenChange, seriesId, onSeriesCancelled }) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!seriesId || !open) return;
    setLoading(true);
    api.get(`/meetings/series/${seriesId}`)
      .then(data => setMeetings(data.meetings || []))
      .catch(() => toast.error('Failed to load series'))
      .finally(() => setLoading(false));
  }, [seriesId, open]);

  const handleCancelFuture = async () => {
    if (!confirm('Cancel all future meetings in this series?')) return;
    setCancelling(true);
    try {
      await api.delete(`/meetings/series/${seriesId}`);
      toast.success('Future meetings cancelled');
      onSeriesCancelled?.();
      onOpenChange(false);
    } catch {
      toast.error('Failed to cancel series');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>🔁 Recurring Series</DialogTitle>
          <DialogDescription>
            {!loading && meetings.length > 0
              ? `${meetings[0].title} — ${meetings.length} occurrence${meetings.length !== 1 ? 's' : ''}`
              : 'Loading series...'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : meetings.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No meetings found in this series.</p>
        ) : (
          <div className="space-y-2">
            {meetings.map(m => (
              <div key={m._id} className="flex items-center justify-between p-3 rounded-lg border">
                <div>
                  <p className="text-sm font-medium">
                    {m.scheduled_at
                      ? `${formatDate(m.scheduled_at)} at ${formatTime(m.scheduled_at)}`
                      : 'No date'}
                  </p>
                  <Badge variant="secondary" className={`text-xs mt-1 ${STATUS_COLORS[m.status] || ''}`}>
                    {m.status}
                  </Badge>
                </div>
                {m.status === 'scheduled' && m.join_code && (
                  <Button variant="ghost" size="sm" asChild>
                    <a href={`/meet/${m.join_code}`}>Join</a>
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleCancelFuture}
            disabled={cancelling || loading}
          >
            {cancelling ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Cancelling...</>
            ) : (
              'Cancel Future Meetings'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
